/**
 * Plain HTTP helpers for the server's REST endpoints (no WebSocket needed), e.g. to show the
 * public lobby list on the main menu before connecting.
 */
import type {
  DailyChallengeResponse,
  DailyScoresResponse,
  DailySubmitRequest,
  DailySubmitResponse,
  LadderPlayerResponse,
  LadderResponse,
  LobbySummary,
} from "../../shared/protocol";
import type { RunningGameSummary } from "../../server/types";

export type { RunningGameSummary };

export interface LobbiesResponse {
  lobbies: LobbySummary[];
  /** Server epoch ms at response time (to correct `startsAt` for clock skew). */
  serverTime: number;
}

export interface GamesResponse {
  games: RunningGameSummary[];
  serverTime: number;
}

export interface HealthResponse {
  status: string;
  protocolVersion: number;
  uptimeS: number;
  connections: number;
  games: number;
  serverTime: number;
  [key: string]: unknown;
}

/**
 * An API request failed. `status` is the HTTP status (0 = network failure) and `code` the
 * server's machine-readable error code when it sent one (e.g. "rate_limited", "wrong_settings").
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function errorFrom(res: Response, what: string): Promise<ApiError> {
  let code = `http_${res.status}`;
  let message = `${what} failed: ${res.status}`;
  try {
    const body = (await res.json()) as { error?: unknown; code?: unknown };
    if (typeof body.code === "string") code = body.code;
    if (typeof body.error === "string") message = body.error;
  } catch {
    /* not JSON */
  }
  return new ApiError(message, res.status, code);
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { headers: { Accept: "application/json" }, signal });
  } catch (err) {
    if ((err as Error)?.name === "AbortError") throw err;
    throw new ApiError(`GET ${url} failed: ${(err as Error)?.message ?? err}`, 0, "network");
  }
  if (!res.ok) throw await errorFrom(res, `GET ${url}`);
  return (await res.json()) as T;
}

/** `baseUrl` defaults to the page origin ("" = same origin; Vite proxies /api in dev). */
export function fetchLobbies(baseUrl = "", signal?: AbortSignal): Promise<LobbiesResponse> {
  return getJson<LobbiesResponse>(`${baseUrl}/api/lobbies`, signal);
}

/** Running games (for the spectator browser). */
export function fetchGames(baseUrl = "", signal?: AbortSignal): Promise<GamesResponse> {
  return getJson<GamesResponse>(`${baseUrl}/api/games`, signal);
}

export function fetchHealth(baseUrl = "", signal?: AbortSignal): Promise<HealthResponse> {
  return getJson<HealthResponse>(`${baseUrl}/api/health`, signal);
}

// ---------------------------------------------------------------------------
// Wave 4 (P15): Elo ladder and daily challenge
// ---------------------------------------------------------------------------

/** Ladder page, best first (`limit` max 100); stage J: of one ladder bucket ("simple:conquest", ...). */
export function fetchLadder(opts: { limit?: number; offset?: number; bucket?: string; baseUrl?: string; signal?: AbortSignal } = {}): Promise<LadderResponse> {
  const q = new URLSearchParams();
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  if (opts.bucket !== undefined) q.set("bucket", opts.bucket);
  const qs = q.toString();
  return getJson<LadderResponse>(`${opts.baseUrl ?? ""}/api/ladder${qs ? "?" + qs : ""}`, opts.signal);
}

/** One player's rating, rank and history by PUBLIC id; null when the player is not rated yet. */
export async function fetchLadderPlayer(
  publicId: string,
  opts: { bucket?: string; baseUrl?: string; signal?: AbortSignal } = {},
): Promise<(LadderPlayerResponse & { player: NonNullable<LadderPlayerResponse["player"]> }) | null> {
  const qs = opts.bucket !== undefined ? `?bucket=${encodeURIComponent(opts.bucket)}` : "";
  const res = await getJson<LadderPlayerResponse>(`${opts.baseUrl ?? ""}/api/ladder/${encodeURIComponent(publicId)}${qs}`, opts.signal);
  return res.player ? { ...res, player: res.player } : null;
}

/** Today's daily challenge (UTC). */
export function fetchDailyChallenge(opts: { baseUrl?: string; signal?: AbortSignal } = {}): Promise<DailyChallengeResponse> {
  return getJson<DailyChallengeResponse>(`${opts.baseUrl ?? ""}/api/daily`, opts.signal);
}

/** A day's scoreboard (default today) plus the given player's best entry. */
export function fetchDailyScores(opts: { day?: string; limit?: number; publicId?: string | null; baseUrl?: string; signal?: AbortSignal } = {}): Promise<DailyScoresResponse> {
  const q = new URLSearchParams();
  if (opts.day) q.set("day", opts.day);
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.publicId) q.set("publicId", opts.publicId);
  const qs = q.toString();
  return getJson<DailyScoresResponse>(`${opts.baseUrl ?? ""}/api/daily/scores${qs ? "?" + qs : ""}`, opts.signal);
}

/** Submit a finished daily challenge replay; the server re-simulates it and answers with the verified score. */
export async function submitDailyReplay(body: DailySubmitRequest, opts: { baseUrl?: string; signal?: AbortSignal } = {}): Promise<DailySubmitResponse> {
  const url = `${opts.baseUrl ?? ""}/api/daily/submit`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error)?.name === "AbortError") throw err;
    throw new ApiError(`POST ${url} failed: ${(err as Error)?.message ?? err}`, 0, "network");
  }
  if (!res.ok) throw await errorFrom(res, `POST ${url}`);
  return (await res.json()) as DailySubmitResponse;
}
